import React, { forwardRef, useState } from "react";
import styled from "styled-components";
import { navigateWithInitiateCheckout } from "../services/checkoutTracking";
import { User } from "../types";
import { sha256 } from "js-sha256";
import { LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY } from "../hooks/useAnswerTracker";
import { Model } from "survey-core";
import { CheckoutPlan } from "../LandingPage";

// --- Styled Components Props ---
// Defines props used specifically for conditional styling in styled-components
// We use the '$' prefix for transient props (they are used by styled-components but not passed to the DOM element)
interface PlanCardProps {
  $isSelected: boolean; // True if this card is the currently selected plan
  $hasBadge: boolean; // True if this plan has a badge to display
}

interface CheckoutButtonProps {
  $isDisabled: boolean; // True if no plan is selected, disabling the button
}

// --- Styled Components Definitions ---

// Main container for the entire pricing section
const SectionContainer = styled.section`
  text-align: center;
  padding: 20px; /* Increased padding */
  background-color: #f5f5f5; /* Light grey background */
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%; /* Ensure it takes full width */
  box-sizing: border-box; /* Include padding in width calculation */
  margin-top: 30px;

  @media (min-width: 768px) {
    margin-top: 100px;
  }
`;

// Main heading for the section
const Title = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.5rem); /* Responsive font size */
  color: #1e1e1e; /* Darker blue color */
  padding: 0px;
  margin: 0px;
  font-family: Oswald, sans-serif;
  font-size: 28px;
  color: rgb(30, 30, 30);
  font-weight: 600;
  max-width: 950px;
  @media (min-width: 768px) {
    font-size: 54px;
  }
`;
const TitleSpan = styled.div`
  color: #0b6dff;
`;

// Subtitle text below the main heading
const Subtitle = styled.p`
  font-family: "Lato";
  font-size: 24px; /* Responsive font size */
  color: #4a4a4a;
  font-weight: 500;
  margin-bottom: 24px;
  max-width: 900px; /* Slightly wider max-width */
  line-height: 1.6;
  @media (max-width: 800px) {
    font-size: 16px;
  }

  @media (min-width: 768px) {
    margin-top: 6px;
    margin-bottom: 56px;
  }
`;

// Flex container for the plan cards
const PlansWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 50px; /* Space between cards */
  flex-wrap: wrap; /* Allow cards to wrap onto the next line on smaller screens */
  margin-bottom: 30px;
  width: 100%;
  max-width: 880px; /* Max width for the cards container */
  @media (max-width: 800px) {
    flex-direction: column-reverse;
    padding-left: 10px;
    pading-right: 10px;
    gap: 30px;
    align-items: center;
  }
`;

// Individual plan card styling
const PlanCard = styled.div<PlanCardProps>`
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) => (props.$isSelected ? "#F8FBFF" : "#ffffff")};
  /* Conditional border based on selection */
  border: 4px solid ${(props) => (props.$isSelected ? "#E7F0FF" : "white")};
  border-radius: 20px;

  padding: 36px 24px;

  /* Responsive width with a max */
  /* Conditional box-shadow for selection */
  box-shadow: ${(props) =>
    props.$isSelected
      ? "0 10px 25px rgba(64, 109, 223, 0.16)"
      : "0 5px 15px rgba(0, 0, 0, 0.08)"};
  cursor: pointer;
  /* Smooth transitions for hover and selection effects */
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative; /* Required for absolute positioning of the badge */
  text-align: center;
  display: flex;
  flex-direction: column;
  /* Hover effect */

  /* Focus outline for accessibility */
  &:focus {
    outline: 2px solid rgb(64, 110, 223);
    outline-offset: 3px;
  }
  &:focus:not(:focus-visible) {
    outline: none; /* Remove outline for mouse users */
  }

  @media (min-width: 768px) {
    padding: 28px
  }
`;

const PlanBadgeWeb13 = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 10px;
  background: #E7F0FF;
  border-radius: 95.5168px;

  font-family: 'Oswald';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  color: #0B6DFF;

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 22px;
  }
`;

// Styling for the icon area
const PlanIcon = styled.div`
  font-size: 3em; /* Larger icon */
  margin-bottom: 0px;

  color: #007bff; /* Blue icon color */
  line-height: 1; /* Prevent extra space */

  width: 36px;
  height: 36px;

  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
  }

  & > svg {
    width: 100%;
    height: 100%;
  }
`;
const PlanIconPlus = styled.div`
  color: #007bff; /* Blue icon color */
  line-height: 1; /* Prevent extra space */
`;

// Styling for the card's title
const CardTitle = styled.h3<PlanCardProps>`
  margin-top: 16px;
  margin-bottom: 16px;

  font-family: 'Lato';
  font-style: normal;
  font-weight: 800;
  font-size: 28px;
  line-height: 120%;
  text-align: center;
  letter-spacing: 0.01em;
  color: #1A1A1A;

  @media (min-width: 768px) {
    font-size: 42px;
    margin-bottom: 24px;
  }
`;

const PriceWrap = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  @media (min-width: 768px) {
    margin-top: 24px;
    gap: 8px;
  }
`;

// Container for price elements
const PlanPrice = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  font-family: 'Lato';
  font-style: normal;
  font-weight: 700;
  font-size: 36px;
  line-height: 34px;
  text-align: center;
  color: #0B6DFF;
  margin-top: 0;

  @media (min-width: 768px) {
    font-size: 48px;
    line-height: 43px;
  }
`;

// Styling for the main price amount
const PriceAmount = styled.span`
  margin-right: 0;
`;

// Styling for additional price details (e.g., "/month")
const PriceDetails = styled.span`
  font-size: 16px;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

// Styling specific to the billing details text (e.g., "billed annually")
const PlanBillingDetails = styled.p`
  font-family: 'Lato';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  text-align: center;
  color: #7A7A7A;
  margin-top: 8px;
  margin-bottom: 0px;

  @media (min-width: 768px) {
    font-size: 16px;
    line-height: 19px;
  }
`;

// Styling for the main description text
const PlanDescription = styled.p`
  font-family: 'Lato';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  color: #7A7A7A;
  margin: 0;



  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 150%;
  }
`;

// Container for the main button and trial text
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  @media (min-width: 768px) {
    margin-top: 24px;
  }
  width: 100%;
`;

// Styling for the main checkout button (using an anchor tag)
export const CheckoutButton = styled.a<CheckoutButtonProps>`
  width: 100%;
  box-sizing: border-box;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 0px;

  background: #DFB140;
  border-radius: 8px;

  font-family: 'Oswald';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 37px;
  text-align: center;
  color: #000000;
  text-transform: uppercase;

  @media (min-width: 768px) {
    font-size: 32px;
    line-height: 48px;
    padding: 16px 0px;
  }

  text-decoration: none; /* Remove underline from link */
  display: inline-block;
  transition: background-color 0.3s ease, opacity 0.3s ease, transform 0.2s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  /* Conditional styles for disabled state */
  opacity: ${(props) => (props.$isDisabled ? 0.6 : 1)};
  cursor: ${(props) => (props.$isDisabled ? "not-allowed" : "pointer")};
  /* Prevent clicking/interaction when disabled */
  pointer-events: ${(props) => (props.$isDisabled ? "none" : "auto")};

  /* Hover effect only when not disabled */
  &:hover {
    background-color: rgb(64, 110, 223);
    transform: ${(props) =>
      !props.$isDisabled
        ? "translateY(-2px)"
        : "none"}; /* Slight lift on hover */
    color: white;
    box-shadow: ${(props) =>
      !props.$isDisabled
        ? "0 6px 12px rgba(0, 0, 0, 0.15)"
        : "0 4px 10px rgba(0, 0, 0, 0.1)"};
  }

  /* Focus outline for accessibility */
  &:focus {
    outline: 2px solid #dfb140; /* Outline matching button color */
    outline-offset: 3px;
  }
  &:focus:not(:focus-visible) {
    outline: none; /* Remove outline for mouse users */
  }
`;

// Styling for the "free trial" text below the button
const TrialText = styled.p`
  font-family: 'Lato';
  font-style: italic;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  color: #4A4A4A;
  margin-top: 12px;

  @media (min-width: 768px) {
    font-size: 16px;
    line-height: 18px;
  }
`;

const Section1 = styled.div`
    display: flex;
    justify-content: center;
`;
const Section2 = styled.div``;
const Section4 = styled.div`
  border-bottom: 1px solid #E9E9EF;
  padding-bottom: 12px;
  @media (min-width: 768px) {
    padding-bottom: 16px;
    min-height: 135px;
  }
`;
const Section5 = styled.div``;
const Section6 = styled.div``;

// --- Trial Offer Section (single plan card) ---
const TrialOfferCard = styled.div`
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  padding: 24px;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 32px;
    max-width: 80%;
  }

  &+& {
    margin-top: 12px;
    padding: 16px;
    @media (min-width: 768px) {
      margin-top: 24px;
      padding: 16px;
    }
  }
`;

const TrialTimeline = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
  padding-left: 21px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 32px;
    margin-bottom: 24px;
    padding-left: 0;
  }
`;

const TrialTimelineItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  z-index: 1;

  @media (min-width: 768px) {
    flex: 1;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
  }
`;

const TrialTimelineConnector = styled.div<{ isSecond?: boolean }>`
  display: none;

  @media (min-width: 768px) {
    position: absolute;
    left: 50%;
    display: block;
    height: 6px;
    background-image: ${props => props.isSecond ? 'linear-gradient(to right, #418BFC, #8EB6F8)' : 'linear-gradient(to right, #0B6DFF, #418BFC)'};
    margin-top: 20px;
    width: 100%;
  }
`;

const TrialTimelineIconWrapper = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #0b6dff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-left: -22px; /* Center icon on vertical line */
  z-index: 1;
  @media (min-width: 768px) {
    margin-left: 0;
  }
`;

const TrialTimelineContent = styled.div`
  flex: 1;
  text-align: left;
`;

const TrialTimelineContentTop = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: left;

  @media (min-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const TrialTimelineDatePill = styled.span`
  display: inline-block;
  background: #E7F0FF;
  color: #4a4a4a;
  font-family: 'Lato';
  font-size: 14px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  margin-left: 8px;

  @media (min-width: 768px) {
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
`;

const TrialTimelineTitle = styled.h4`
  font-family: 'Lato';
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: inline;

  @media (min-width: 768px) {
    display: block;
    font-size: 28px;
    margin-top: 4px;
  }
`;

const TrialTimelineDescription = styled.p`
  font-family: 'Lato';
  font-size: 14px;
  font-weight: 500;
  color: #7a7a7a;
  line-height: 1.5;
  margin: 8px 0 0 0;

  @media (min-width: 768px) {
    font-size: 16px;
    text-align: center;
    margin-top: 8px;
    max-width: 300px;
  }
`;

const TrialVerticalLine = styled.div<{ isSecond?: boolean }>`
  position: absolute;
  left: -3px;
  top: 40px;
  bottom: -30px;
  width: 6px;
  background: #0b6dff;
  z-index: 0;
  background-image: ${props => props.isSecond ? 'linear-gradient(to bottom, #418BFC, #8EB6F8)' : 'linear-gradient(to bottom, #0B6DFF, #418BFC)'};

  @media (min-width: 768px) {
    display: none;
  }
`;


const TrialPricingSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const TrialPricingLabel = styled.p`
  font-family: 'Lato';
  font-size: 14px;
  font-weight: 500;
  color: #555;
  margin: 0;
  line-height: 1.25;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const OldPrice = styled.div`
  font-family: 'Lato';
  font-size: 13px;
  font-weight: 500;
  color: #7a7a7a;
  text-decoration: line-through;
  margin: 0 0 2px;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const DiscountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  gap: 8px;
  border-radius: 100px;
  background: #E3F5EA;
  color: #317A40;
  font-family: 'Lato';
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  line-height: 150%;
  text-transform: uppercase;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const TrialPricingMainLine = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 8px;
  row-gap: 4px;
`;

const TrialPricingValue = styled.span`
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 4px;
  row-gap: 4px;
`;

const TrialPricingDiscount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 100px;
  background: #e3f5ea;
  font-family: 'Lato';
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  color: #317a40;
  text-transform: uppercase;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const TrialPricingAmount = styled.span`
  font-family: 'Lato';
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;

  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const TrialPricingDetail = styled.span`
  font-family: 'Lato';
  font-size: 14px;
  font-weight: 500;
  color: #7a7a7a;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const TrialRegularPrice = styled.div`
  font-family: 'Lato';
  font-size: 14px;
  font-weight: 500;
  color: #7a7a7a;
  margin-top: 8px;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const TrialRegularPriceValue = styled.span`
  text-decoration: line-through;
`;

const TrialCtaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding: 0 16px;
  width: 100%;
  max-width: 560px;
  @media (min-width: 768px) {
    margin-top: 42px;
    gap: 24px;
    max-width: 350px;
  }
`;

const TrialViewAllPlans = styled.button`
  font-family: 'Lato';
  font-size: 14px;
  font-weight: 500;
  color: #7a7a7a;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  margin: 0;

  &:hover {
    color: #1a1a1a;
  }
`;

const parsePrice = (price?: string) => {
  if (!price) {
    return null;
  }

  const value = Number(price.replace(/[^0-9.]/g, ""));
  return Number.isFinite(value) && value > 0 ? value : null;
};

const getDiscountLabel = (oldPrice?: string, currentPrice?: string) => {
  const oldValue = parsePrice(oldPrice);
  const currentValue = parsePrice(currentPrice);

  if (oldValue == null || currentValue == null || currentValue >= oldValue) {
    return null;
  }

  const rawDiscount = ((oldValue - currentValue) / oldValue) * 100;
  const roundedDiscount = Math.round(rawDiscount / 5) * 5;

  return `${roundedDiscount}% OFF`;
};

interface PricingPlansProps {
  plans: CheckoutPlan[]; // Array of plan objects, including checkout URLs
  user?: User; // Optional user data to pass to checkout URL
  survey?: Model;
  onViewAllPlans?: () => void;
}
// --- React Component ---
const PricingPlansWeb20 = forwardRef<HTMLDivElement, PricingPlansProps>(
  ({ plans, user, onViewAllPlans }, ref) => {

    const getCheckoutUrl = (checkoutUrl: string, plan: CheckoutPlan) => {
      if (!plan.isOneTime) {
        return "/checkout?plan=" + plan.id;
      }
    
      // Replace with internal service urls remove this when we replace all links
      if (localStorage.getItem("checkoutv2") !== "enabled") {
        return checkoutUrl;
      }
      const urlObj = new URL(checkoutUrl);
      if (urlObj.hostname === "api.builtwithscience.com"
        && urlObj.pathname.startsWith("/production/gateway/v2/stripe/")
      ) {
        urlObj.hostname = urlObj.hostname.replace(
          "api.builtwithscience.com",
          "core.builtwithscience.com"
        );
        urlObj.pathname = urlObj.pathname.replace(
          "/production/gateway/v2/stripe/",
          "/internal/public/checkout/web/"
        );
      }

      const iacConnectionId = localStorage.getItem("iacConnectionId");
      const mode = localStorage.getItem("mode");
      if (
        iacConnectionId &&
        mode === "webview"
      ) {
        // Replace mode from "web" to "webview" in the URL
        urlObj.pathname = urlObj.pathname.replace(
          "/internal/public/checkout/web/",
          "/internal/public/checkout/webview/"
        );
        urlObj.searchParams.set("connectionId", iacConnectionId);
      }

      // Add email to URL if available
      if (user?.email) {
        urlObj.searchParams.set("email", sha256(user.email));
      }

      if (user?.name) {
        urlObj.searchParams.set("name", user.name);
      }

      const sessionIdentitySessionId = localStorage.getItem(
          LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY
      );
      if (sessionIdentitySessionId) {
          urlObj.searchParams.set(
              "session_identity_session_id",
              sessionIdentitySessionId
          );
      }

      return urlObj.toString();
    };

    const [showSinglePlanCard, setShowSinglePlanCard] = useState(true);
    const yearlyPlan = plans.find(plan => plan.id === "committed");
    const yearlyDiscountLabel = getDiscountLabel(
      yearlyPlan?.oldPrice,
      yearlyPlan?.price
    );
  
    if (!yearlyPlan) {
      return null;
    }

    // --- Render ---
    return (
      <SectionContainer id="pricingbox" ref={ref}>
        <Title>
          TRY YOUR PERSONAL PLAN <TitleSpan>FREE FOR 14 DAYS</TitleSpan>
        </Title>
        <Subtitle>
          Cancel anytime with no charge before your trial ends
        </Subtitle>

        {showSinglePlanCard ? (
          <>
            <TrialOfferCard id="committed">
              <TrialTimeline>
                <TrialTimelineItem>
                  <TrialVerticalLine />
                  <TrialTimelineConnector />
                  <TrialTimelineIconWrapper>
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1.8873" y="1.8873" width="56.2254" height="56.2254" rx="28.1127" fill="#0B6DFF"/>
                      <rect x="1.8873" y="1.8873" width="56.2254" height="56.2254" rx="28.1127" stroke="white" stroke-width="3.77461"/>
                      <path d="M21.5469 23.999C21.5469 23.7338 21.6522 23.4795 21.8398 23.2919C22.0273 23.1044 22.2817 22.999 22.5469 22.999H36.5469C36.8121 22.999 37.0664 23.1044 37.254 23.2919C37.4415 23.4795 37.5469 23.7338 37.5469 23.999V27.999H21.5469V23.999Z" stroke="white" stroke-width="2.20834" stroke-linejoin="round"/>
                      <path d="M25.5391 24.499V21.499M33.5391 24.499V21.499" stroke="white" stroke-width="2.20834" stroke-linecap="round"/>
                      <path d="M21.5469 27.9985H37.5469V36.9985C37.5469 37.2638 37.4415 37.5181 37.254 37.7056C37.0664 37.8932 36.8121 37.9985 36.5469 37.9985H22.5469C22.2817 37.9985 22.0273 37.8932 21.8398 37.7056C21.6522 37.5181 21.5469 37.2638 21.5469 36.9985V27.9985Z" stroke="white" stroke-width="2.20834" stroke-linejoin="round"/>
                    </svg>
                  </TrialTimelineIconWrapper>
                  <TrialTimelineContent>
                    <TrialTimelineContentTop>
                      <TrialTimelineDatePill>
                        {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </TrialTimelineDatePill>
                      <TrialTimelineTitle>Today</TrialTimelineTitle>
                    </TrialTimelineContentTop>
                    <TrialTimelineDescription>
                      Get instant access to your plan with personalized workouts, nutrition and expert guidance.
                    </TrialTimelineDescription>
                  </TrialTimelineContent>
                </TrialTimelineItem>
                <TrialTimelineItem>
                  <TrialVerticalLine isSecond />
                  <TrialTimelineConnector isSecond />
                  <TrialTimelineIconWrapper>
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1.42939" y="1.42939" width="57.1412" height="57.1412" rx="28.5706" fill="#418BFC"/>
                    <rect x="1.42939" y="1.42939" width="57.1412" height="57.1412" rx="28.5706" stroke="white" stroke-width="2.85879"/>
                    <path d="M28.068 21.2479C28.2174 20.8786 28.4737 20.5624 28.804 20.3397C29.1344 20.117 29.5237 19.998 29.922 19.998C30.3204 19.998 30.7097 20.117 31.04 20.3397C31.3703 20.5624 31.6266 20.8786 31.776 21.2479C33.255 21.6542 34.5597 22.5349 35.4894 23.7548C36.4191 24.9747 36.9225 26.4661 36.922 27.9999V32.6969L38.754 35.4449C38.8545 35.5955 38.9122 35.7706 38.921 35.9514C38.9298 36.1322 38.8893 36.3121 38.8039 36.4717C38.7185 36.6313 38.5913 36.7648 38.436 36.8578C38.2807 36.9508 38.1031 36.9999 37.922 36.9999H33.387C33.2666 37.833 32.8501 38.5949 32.2137 39.1459C31.5774 39.6969 30.7638 40.0001 29.922 40.0001C29.0803 40.0001 28.2667 39.6969 27.6303 39.1459C26.994 38.5949 26.5774 37.833 26.457 36.9999H21.922C21.741 36.9999 21.5633 36.9508 21.408 36.8578C21.2527 36.7648 21.1256 36.6313 21.0402 36.4717C20.9547 36.3121 20.9143 36.1322 20.9231 35.9514C20.9318 35.7706 20.9895 35.5955 21.09 35.4449L22.922 32.6969V27.9999C22.922 24.7759 25.102 22.0599 28.068 21.2479ZM28.508 36.9999C28.6113 37.2926 28.8028 37.546 29.0561 37.7252C29.3095 37.9045 29.6122 38.0007 29.9225 38.0007C30.2329 38.0007 30.5356 37.9045 30.7889 37.7252C31.0422 37.546 31.2337 37.2926 31.337 36.9999H28.508ZM29.922 22.9999C28.5959 22.9999 27.3242 23.5267 26.3865 24.4644C25.4488 25.4021 24.922 26.6738 24.922 27.9999V32.9999C24.9221 33.1975 24.8636 33.3906 24.754 33.5549L23.791 34.9999H36.052L35.089 33.5549C34.9798 33.3905 34.9217 33.1974 34.922 32.9999V27.9999C34.922 26.6738 34.3952 25.4021 33.4576 24.4644C32.5199 23.5267 31.2481 22.9999 29.922 22.9999Z" fill="white"/>
                  </svg>

                  </TrialTimelineIconWrapper>
                  <TrialTimelineContent>
                    <TrialTimelineContentTop>
                      <TrialTimelineDatePill>
                        {(() => {
                          const d = new Date();
                          d.setDate(d.getDate() + 12);
                          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        })()}
                      </TrialTimelineDatePill>
                      <TrialTimelineTitle>Day 12</TrialTimelineTitle>
                    </TrialTimelineContentTop>
                    <TrialTimelineDescription>
                      We'll send you a quick email reminder before your free trial ends, so you know it's coming up.
                    </TrialTimelineDescription>
                  </TrialTimelineContent>
                </TrialTimelineItem>
                <TrialTimelineItem>
                  <TrialTimelineIconWrapper>
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1.42939" y="1.42939" width="57.1412" height="57.1412" rx="28.5706" fill="#8EB6F8"/>
                    <rect x="1.42939" y="1.42939" width="57.1412" height="57.1412" rx="28.5706" stroke="white" stroke-width="2.85879"/>
                    <path d="M30.6711 21.0005C30.8567 21.0006 31.0387 21.0524 31.1966 21.15C31.3545 21.2477 31.4821 21.3874 31.5651 21.5535L32.2891 23.0005H38.2891C38.5543 23.0005 38.8086 23.1058 38.9962 23.2934C39.1837 23.4809 39.2891 23.7353 39.2891 24.0005V35.0005C39.2891 35.2657 39.1837 35.5201 38.9962 35.7076C38.8086 35.8951 38.5543 36.0005 38.2891 36.0005H31.9071C31.7214 36.0004 31.5394 35.9486 31.3815 35.8509C31.2236 35.7533 31.0961 35.6136 31.0131 35.4475L30.2891 34.0005H23.2891V40.0005H21.2891V21.0005H30.6711ZM30.0531 23.0005H23.2891V32.0005H31.5251L32.5251 34.0005H37.2891V25.0005H31.0531L30.0531 23.0005Z" fill="white"/>
                  </svg>

                  </TrialTimelineIconWrapper>
                  <TrialTimelineContent>
                    <TrialTimelineContentTop>
                      <TrialTimelineDatePill>
                        {(() => {
                          const d = new Date();
                          d.setDate(d.getDate() + 14);
                          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        })()}
                      </TrialTimelineDatePill>
                      <TrialTimelineTitle>Day 14</TrialTimelineTitle>
                    </TrialTimelineContentTop>
                    <TrialTimelineDescription>
                      If you choose to continue, you'll be charged the subscription amount. Cancel anytime before.
                    </TrialTimelineDescription>
                  </TrialTimelineContent>
                </TrialTimelineItem>
              </TrialTimeline>
            </TrialOfferCard>
              <TrialOfferCard>
                <TrialPricingSection>
                  <TrialPricingLabel>14-day trial, then</TrialPricingLabel>
                  <TrialPricingMainLine>
                    {yearlyDiscountLabel ? (
                      <TrialPricingDiscount>{yearlyDiscountLabel}</TrialPricingDiscount>
                    ) : null}
                    <TrialPricingValue>
                      <TrialPricingAmount>{yearlyPlan.price}</TrialPricingAmount>
                      {yearlyPlan.priceDetails ? (
                        <TrialPricingAmount>
                          {yearlyPlan.priceDetails.replace(/\s/g, "")}
                        </TrialPricingAmount>
                      ) : null}
                      <TrialPricingDetail>(billed annually)</TrialPricingDetail>
                    </TrialPricingValue>
                  </TrialPricingMainLine>
                  {yearlyPlan.oldPrice ? (
                    <TrialRegularPrice>
                      Regular Price:{" "}
                      <TrialRegularPriceValue>
                        {yearlyPlan.oldPrice}
                        {yearlyPlan.priceDetails?.replace(/\s/g, "")}
                      </TrialRegularPriceValue>
                    </TrialRegularPrice>
                  ) : null}
                </TrialPricingSection>
              </TrialOfferCard>
            <TrialCtaWrapper>
              <CheckoutButton
                href={getCheckoutUrl(yearlyPlan.checkoutUrl, yearlyPlan)}
                onClick={(e) => {
                  e.preventDefault();
                  navigateWithInitiateCheckout(getCheckoutUrl(yearlyPlan.checkoutUrl, yearlyPlan));
                }}
                // Pass transient prop for disabled styling
                $isDisabled={false}
                // Open link in a new tab for external checkout pages (optional)
                target="_self"
                rel="noopener"
              >
                Start Free Trial
              </CheckoutButton>
              <TrialViewAllPlans
                onClick={() => {
                  if (onViewAllPlans) {
                    onViewAllPlans();
                    return;
                  }
                  setShowSinglePlanCard(false);
                }}
              >
                View all plans
              </TrialViewAllPlans>
            </TrialCtaWrapper>
          </>
        ) : (
          <PlansWrapper>
            {/* Map over the plans data to render each card */}
            {plans.map((plan, index) => {
              const mode = localStorage.getItem("mode");
              const discountLabel = getDiscountLabel(plan.oldPrice, plan.price);
              if (
                (mode === "inapp" || mode === "webview") &&
                plan.id === "base"
              ) {
                return null;
              }
              return (
                <div style={{ flex: 1 }} id={plan.id}>
                  <PlanCard
                    key={plan.id}
                    // Pass transient props for conditional styling
                    $isSelected={false}
                    $hasBadge={!!plan.badge} // Pass true if badge exists, false otherwise
                  >
                    <Section1>
                      {plan.id === "committed" ? (
                        <PlanBadgeWeb13>
                          {plan.badge}
                          <PlanIconPlus>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10.1465 3.79102H17.8535C18.0635 3.79102 18.2443 3.79102 18.4077 3.81085C18.993 3.88375 19.5367 4.15166 19.9511 4.57138C20.3655 4.99109 20.6265 5.53816 20.692 6.12435H21.6323C21.8797 6.12202 22.4222 6.11735 22.8702 6.41718C23.45 6.80335 23.625 7.46718 23.625 8.16602C23.625 11.3464 21.9298 13.4697 19.7657 14.6492C18.5582 16.8472 16.3275 18.3744 14 18.3744C12.2278 18.3744 10.5467 17.4154 9.33683 16.0889C8.93483 15.6503 8.58196 15.1691 8.2845 14.6539C6.1215 13.4802 4.375 11.3592 4.375 8.16602C4.375 7.46602 4.55 6.80335 5.12867 6.41718C5.57783 6.11735 6.12033 6.12202 6.36767 6.12435H7.308L7.3115 6.09168C7.38363 5.5119 7.64713 4.97274 8.06026 4.55961C8.47339 4.14648 9.01255 3.88298 9.59233 3.81085C9.75567 3.79102 9.9365 3.79102 10.1465 3.79102ZM9.04167 6.99935V7.00285L9.03 11.0827V11.0839C9.03 11.925 9.27033 12.7849 9.68683 13.5724L9.70317 13.6015C9.954 14.0705 10.2678 14.5115 10.6307 14.9082C11.613 15.9862 12.8567 16.6244 14.0012 16.6244C15.6602 16.6244 17.409 15.425 18.3178 13.6564C18.7307 12.8612 18.9467 11.9786 18.9478 11.0827L18.9455 11.0804L18.9595 6.69252C18.9595 6.41018 18.9572 6.34835 18.9525 6.30752C18.9283 6.11451 18.8405 5.93506 18.703 5.79752C18.5655 5.65998 18.386 5.57218 18.193 5.54802C18.0648 5.54039 17.9364 5.53805 17.808 5.54102H10.1932C9.90967 5.54102 9.849 5.54218 9.80817 5.54802C9.61516 5.57218 9.43571 5.65998 9.29817 5.79752C9.16063 5.93506 9.07283 6.11451 9.04867 6.30752C9.04104 6.4357 9.0387 6.56414 9.04167 6.69252V6.99935ZM20.706 7.87435L20.6955 11.0827V11.0839M20.6687 11.7127C21.413 10.8167 21.8762 9.64768 21.8762 8.16602C21.8762 8.02835 21.8645 7.93735 21.854 7.88018C21.7643 7.87478 21.6744 7.87283 21.5845 7.87435H20.706M7.30333 11.645C7.28573 11.4581 7.27717 11.2704 7.27767 11.0827V11.0804L7.28817 7.87435H6.41667C6.2895 7.87435 6.209 7.87435 6.14717 7.88018C6.13077 7.97455 6.12335 8.07025 6.125 8.16602C6.125 9.60918 6.57767 10.756 7.30333 11.645ZM14 18.9577C14.2321 18.9577 14.4546 19.0499 14.6187 19.214C14.7828 19.3781 14.875 19.6006 14.875 19.8327V22.4577H18.6667C18.8987 22.4577 19.1213 22.5499 19.2854 22.714C19.4495 22.8781 19.5417 23.1006 19.5417 23.3327C19.5417 23.5648 19.4495 23.7873 19.2854 23.9514C19.1213 24.1155 18.8987 24.2077 18.6667 24.2077H9.33333C9.10127 24.2077 8.87871 24.1155 8.71461 23.9514C8.55052 23.7873 8.45833 23.5648 8.45833 23.3327C8.45833 23.1006 8.55052 22.8781 8.71461 22.714C8.87871 22.5499 9.10127 22.4577 9.33333 22.4577H13.125V19.8327C13.125 19.6006 13.2172 19.3781 13.3813 19.214C13.5454 19.0499 13.7679 18.9577 14 18.9577Z" fill="#0B6DFF"/>
                              <path d="M13.7308 7.64806C13.7533 7.59545 13.7906 7.55058 13.8383 7.51905C13.8861 7.48752 13.942 7.4707 13.9992 7.4707C14.0564 7.4707 14.1123 7.48752 14.16 7.51905C14.2077 7.55058 14.2451 7.59545 14.2675 7.64806L14.8567 9.06206C14.8772 9.11141 14.911 9.15416 14.9542 9.18564C14.9974 9.21712 15.0484 9.23612 15.1017 9.24056L16.6288 9.36306C16.6863 9.36761 16.7411 9.38907 16.7864 9.42474C16.8316 9.46041 16.8653 9.50869 16.8831 9.56348C16.901 9.61827 16.9022 9.67712 16.8866 9.73261C16.8711 9.78809 16.8394 9.83772 16.7957 9.87523L15.6325 10.8716C15.5918 10.9065 15.5616 10.9521 15.5452 11.0031C15.5287 11.0542 15.5266 11.1087 15.5392 11.1609L15.8938 12.6496C15.9075 12.7057 15.9042 12.7647 15.8843 12.819C15.8644 12.8733 15.8288 12.9204 15.782 12.9544C15.7352 12.9884 15.6794 13.0078 15.6216 13.01C15.5639 13.0122 15.5067 12.9972 15.4575 12.9669L14.1508 12.1689C14.1051 12.1411 14.0527 12.1264 13.9992 12.1264C13.9457 12.1264 13.8932 12.1411 13.8475 12.1689L12.5408 12.9669C12.4916 12.9972 12.4345 13.0122 12.3767 13.01C12.3189 13.0078 12.2631 12.9884 12.2163 12.9544C12.1696 12.9204 12.134 12.8733 12.1141 12.819C12.0942 12.7647 12.0908 12.7057 12.1045 12.6496L12.4603 11.1609C12.4729 11.1087 12.4708 11.0542 12.4543 11.0031C12.4379 10.9521 12.4077 10.9065 12.367 10.8716L11.2027 9.87523C11.1585 9.8378 11.1265 9.78809 11.1107 9.73242C11.0949 9.67675 11.096 9.61764 11.1139 9.5626C11.1318 9.50756 11.1656 9.45909 11.2112 9.42335C11.2567 9.38761 11.3118 9.36621 11.3695 9.36189L12.8955 9.24056C12.949 9.23632 13.0003 9.21742 13.0437 9.18593C13.0871 9.15444 13.121 9.11158 13.1417 9.06206L13.7308 7.64806Z" fill="#0B6DFF"/>
                            </svg>
                          </PlanIconPlus>
                        </PlanBadgeWeb13>
                      ) : (
                        <PlanIcon>{plan.icon}</PlanIcon>
                      )}
                    </Section1>
                    <Section2>
                      <CardTitle
                        $hasBadge={!!plan.badge}
                        $isSelected={false}
                      >
                        {plan.title}
                      </CardTitle>
                    </Section2>

                    <Section5>
                      <PlanDescription>{plan.description}</PlanDescription>
                    </Section5>

                    <Section4>
                      <PriceWrap>
                        {plan.oldPrice ? (
                          <OldPrice>
                            {plan.oldPrice}
                            {plan.priceDetails}
                          </OldPrice>
                        ) : null}
                        <PlanPrice>
                          <PriceAmount>
                            {plan.price ?? ""}
                          </PriceAmount>
                          {plan.priceDetails && (
                            <PriceDetails>{plan.priceDetails}</PriceDetails>
                          )}
                        </PlanPrice>
                        {discountLabel ? (
                          <DiscountPill>{discountLabel}</DiscountPill>
                        ) : null}
                      </PriceWrap>
                      {plan.id === "committed" && (
                        <PlanBillingDetails>(billed annually)</PlanBillingDetails>
                      )}
                    </Section4>

                    {/* <ChallengeButton isSelected={isProject60Selected} setIsSelected={handleProject60Change} /> */}

                    <Section6>
                      <ButtonContainer>
                        {/* The main call-to-action button */}
                        <CheckoutButton
                          href={getCheckoutUrl(plan.checkoutUrl, plan)}
                          onClick={(e) => {
                            e.preventDefault();
                            navigateWithInitiateCheckout(getCheckoutUrl(plan.checkoutUrl, plan));
                          }}
                          // Pass transient prop for disabled styling
                          $isDisabled={false}
                          // Open link in a new tab for external checkout pages (optional)
                          target="_self"
                          rel="noopener"
                        >
                          Start Free Trial
                        </CheckoutButton>
                        <TrialText>
                          14 Day Free Trial. Cancel Anytime
                        </TrialText>
                      </ButtonContainer>
                    </Section6>
                  </PlanCard>
                </div>
              );
            })}
          </PlansWrapper>
        )}
      </SectionContainer>
    );
  }
);
export default PricingPlansWeb20;
